// ─── Il calendario: la schermata che vale metà del lavoro (§ 03.3) ──────────
//
// ⭐ L'INDIRIZZO DESCRIVE LA SCHERMATA: `/calendario?data=2026-09-15&campo=…`
// si ricarica, si mette fra i preferiti e si manda a un collega. Sul gestionale
// di oggi non si può, ed è una di quelle mancanze che nessuno segnala e tutti
// aggirano.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CAPIENZA,
  ORARIO_CIRCOLO_PREDEFINITO,
  dettagliCaparraDaScegliereSchema,
  giorniDellaSerie,
  mesiDelCorso,
  oraPiuMinuti,
  prezzoDaListinoLezioni,
  siPrezzaColListinoLezioni,
  sociGiaInCampo,
  vuoleMaestro,
  type Campo,
  type LivelloCorso,
  type CreaManutenzione,
  type CreaPrenotazione,
  type CreaSerieChiesta,
  type GiornoSaltato,
  type Maestro,
  type Prenotazione,
  type CaparraDallaSegreteria,
  type DettagliCaparraDaScegliere,
  type StatoDelLettore,
} from '@pmo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { ErroreApi, chiama } from '../../lib/api.js';
import { esitoDelGuasto, mostraEsito } from '../../lib/esiti.js';
import { GiornoScelto } from '../../components/GiornoScelto.js';
import {
  giornoBreve,
  giornoDellaSettimana,
  giornoPerEsteso,
  mesePerEsteso,
  oggi,
  piuGiorni,
} from '../../lib/time.js';
import { usaLive } from '../../features/live/usaLive.js';
import {
  usaCalendario,
  usaCreazione,
  usaCreazioneSerie,
  usaCreazioneManutenzione,
  usaSpostamento,
  usaTariffeLezioni,
} from '../../features/calendario/dati.js';
import { CampoData } from '../../components/CampoData.js';
import { Griglia, VISTE, etichettaTipo, type Vista } from '../../features/calendario/Griglia.js';
import { Scheda } from '../../features/calendario/Scheda.js';
import { CampoInagibile } from '../../features/calendario/CampoInagibile.js';
import { DaSegnare } from '../../features/calendario/DaSegnare.js';
import { InvitoBot } from '../soci/InvitoBot.js';
import { Pannello, TestaPannello } from '../../features/calendario/Pannello.js';
import {
  AggiungiGiocatore,
  AvvisoMaestroAltrove,
  CampoNote,
  ModuloDettagli,
  ModuloRipetizione,
  cosaManca,
  finePredefinita,
  type ValoriDettagli,
  type ValoriRipetizione,
} from '../../features/calendario/pezzi-scheda.js';
import { euro, usaModifica } from '../impostazioni/comune.js';

/**
 * ⭐ LA VISTA SI RICORDA NEL BROWSER, NON NELL'INDIRIZZO — e la differenza è di
 * significato, non di comodità.
 *
 * Tutto il resto di questa schermata sta nell'indirizzo apposta (⇒ in testa al
 * file): la data e il campo **descrivono la giornata**, e un collegamento
 * mandato a un collega deve mostrargli la stessa cosa. Lo zoom no: descrive
 * **questo schermo**. Metterlo nell'indirizzo vorrebbe dire che chi riceve il
 * collegamento si vede imporre l'ingrandimento di un altro monitor.
 *
 * ⚠️ `localStorage` può lanciare (finestra anonima, dati del sito bloccati): si
 * prova, e se non si può la schermata funziona lo stesso con la vista normale.
 */
/**
 * ⭐⭐ QUANTO DURA UNA PRENOTAZIONE NUOVA — e NON È IL PASSO DELLA GRIGLIA.
 *
 * 🗣️ *«le fasce prenotabili sono da 90 minuti, ma si può iniziare… ogni 30
 * minuti»* — correzione sua dell'11/09, e questa riga è la sua conseguenza
 * arrivata fino in fondo. Il **passo** dice dove si comincia; la **durata**
 * quanto si sta, ed è libera (⇒ `griglia.ts`).
 *
 * 📏 Fino al 16/09 il modulo proponeva il passo: col passo a 30 minuti ogni
 * partita nasceva da mezz'ora e andava corretta a mano. Non si era mai visto
 * perché prima il passo era 90, e i due numeri coincidevano per caso.
 */
const DURATA_PREDEFINITA = 90;

const CHIAVE_VISTA = 'pmo.calendario.vista';

/** Il «Crea» sta nel piede del pannello: `form=` lo lega al modulo da fuori. */
const ID_NUOVA = 'nuova-prenotazione';

function vistaRicordata(): Vista {
  try {
    const v = localStorage.getItem(CHIAVE_VISTA);
    if (VISTE.some((x) => x.valore === v)) return v as Vista;
  } catch {
    /* niente memoria: si riparte dalla vista normale, che è già quella giusta */
  }
  return 'intera';
}

interface Nuova {
  courtId: string;
  time: string;
  durationMinutes: number;
  /** ⭐ Quell'ora i soci non la possono prenotare: il modulo deve dirlo. */
  fuoriFascia: boolean;
}

/**
 * «Campo 1», «Campo 1 e Campo 2», «Campo 1, Campo 2 e Campo 3».
 *
 * ⚖️ La «e» prima dell'ultimo e non una fila di virgole: la testata si legge,
 * non si conta. E zero campi ha una sua frase invece di una stringa vuota, o
 * la riga resterebbe monca proprio mentre dice cosa manca.
 */
function descriviCampi(nomi: readonly string[]): string {
  if (nomi.length === 0) return 'nessun campo';
  if (nomi.length === 1) return nomi[0]!;
  return `${nomi.slice(0, -1).join(', ')} e ${nomi[nomi.length - 1]}`;
}

export function Calendario(): React.JSX.Element {
  const [parametri, setParametri] = useSearchParams();
  const data = parametri.get('data') ?? oggi();
  const campoScelto = parametri.get('campo');

  const attaccato = usaLive();
  const { data: calendario, isPending, error } = usaCalendario(data, attaccato);
  const qc = useQueryClient();
  /** 📟 🆕 Fase 3 (R9): la caparra appena nata parte verso il lettore Solo. */
  const mandaAlLettore = (depositId: string, bookingId: string) => {
    void chiama('POST', `/deposits/${depositId}/reader`)
      .catch((e: unknown) => mostraEsito(esitoDelGuasto(e)))
      .finally(() => void qc.invalidateQueries({ queryKey: ['prenotazione', bookingId] }));
  };
  const puoModificare = usaModifica('view_dashboard');
  // 💶 Incassare è un permesso a parte: chi tiene il calendario non tiene per
  // forza la cassa, e il server rifiuterebbe comunque (`manage_payments`).
  const puoIncassare = usaModifica('manage_payments');

  const [vista, setVista] = useState<Vista>(vistaRicordata);
  const [scheda, setScheda] = useState<string | null>(null);
  /**
   * 🔁 I giorni che una serie non ha potuto prendere. Vivono QUI e non dentro la
   * scheda perché li sa solo chi ha fatto la richiesta: la prenotazione che si
   * apre dopo non ne porta traccia, e il calendario mostra solo quello che
   * esiste — mai quello che non è nato.
   * ⚠️ Si svuotano cambiando scheda: sono la risposta a **quel** gesto, e
   *   restare addosso alla prenotazione dopo direbbero una cosa falsa di lei.
   */
  const [saltate, setSaltate] = useState<GiornoSaltato[]>([]);
  const [nuova, setNuova] = useState<Nuova | null>(null);
  /** 🌧️ Il pannello «Campo non utilizzabile» (R1), aperto dalla barra. */
  const [inagibile, setInagibile] = useState(false);
  const [rifiuto, setRifiuto] = useState<string | null>(null);

  /**
   * ⭐⭐ **UN PANNELLO PER VOLTA, E LO DICONO QUESTE DUE RIGHE.**
   *
   * 🗣️ La richiesta da cui nasce tutto: *«mi si apre prima una scheda e poi una
   * seconda scheda»*. Col foglio in mezzo allo schermo i due stati erano
   * indipendenti e potevano essere veri insieme; qui lo spazio è **uno solo**,
   * e chi ci entra fa uscire l'altro.
   * ⚖️ Non è una pulizia: senza, cliccando una cella libera mentre una scheda è
   *   aperta — gesto normalissimo ora che il calendario resta cliccabile — i due
   *   pannelli si disegnerebbero uno sopra l'altro nello stesso posto.
   */
  function apriScheda(id: string): void {
    setNuova(null);
    // ⚠️ I giorni saltati parlano del gesto appena fatto, non della scheda: chi
    //    ne apre un'altra non deve trovarseli addosso. Li rimette chi li ha.
    setSaltate([]);
    setScheda(id);
  }
  function apriNuova(n: Nuova): void {
    setScheda(null);
    setSaltate([]);
    setNuova(n);
  }
  const conPannello = scheda !== null || nuova !== null;

  /**
   * 🔗 **`?scheda=<id>` APRE UNA PRENOTAZIONE DA FUORI.** Oggi ci arriva la
   * Cassa, che da una riga di incasso porta alla partita che l'ha generato.
   *
   * ⚠️ Il parametro si **consuma**: resta nell'indirizzo il tempo di aprire la
   * scheda e poi sparisce. È un'intenzione, non uno stato — e un indirizzo che
   * riapre la stessa scheda a ogni ritorno indietro sarebbe una schermata che
   * non si lascia chiudere.
   *
   * 🔄 **C'era anche `?nuova=<campo>&ora=…`, ed è caduto il 18/09/2026** con
   * l'assistente, che era l'unico a costruirlo: apriva il modulo di creazione
   * già riempito, perché l'assistente non aveva nessuna strada propria per
   * scrivere — solo un collegamento a questa schermata.
   * 📌 *Un ingresso che nessuno imbocca più non è una porta di servizio: è una
   *    porta aperta di cui nessuno controlla più la chiave.*
   */
  useEffect(() => {
    const dellaScheda = parametri.get('scheda');
    if (!dellaScheda) return;

    setNuova(null);
    setScheda(dellaScheda);

    const puliti = new URLSearchParams(parametri);
    puliti.delete('scheda');
    setParametri(puliti, { replace: true });
  }, [parametri, setParametri]);

  const sposta = usaSpostamento(data);
  const crea = usaCreazione(data);
  const creaSerie = usaCreazioneSerie();
  const creaManutenzione = usaCreazioneManutenzione(data);

  function vaiA(nuovaData: string, nuovoCampo?: string | null): void {
    const p = new URLSearchParams(parametri);
    p.set('data', nuovaData);
    if (nuovoCampo === null) p.delete('campo');
    else if (nuovoCampo !== undefined) p.set('campo', nuovoCampo);
    setParametri(p, { replace: false });
  }

  const campi = useMemo(() => {
    if (!calendario) return [];
    if (!campoScelto) return calendario.courts;
    return calendario.courts.filter((c) => c.id === campoScelto);
  }, [calendario, campoScelto]);

  return (
    <section className={conPannello ? 'calendario con-pannello' : 'calendario'}>
      {/*
        ⭐ IL TITOLONE «Calendario» NON C'È PIÙ, dal 16/09/2026: erano ~90 px di
        altezza per dire una cosa che il menu a sinistra dice già — e su questa
        schermata 90 px sono tre ore di giornata. La data resta, perché quella
        **non** la dice nessun altro, e si sposta nella barra dei comandi.
        ⚖️ Vale solo qui: sulle altre schermate il titolo costa niente, perché
        sotto non c'è una griglia che vuole tutta l'altezza della finestra.
      */}
      <div className="barra-calendario">
        <GiornoScelto data={data} vaiA={(d) => vaiA(d)} />

        <div className="navigazione">
          {/*
            ⚖️ I due bottoni portano la freccia e basta, e la data sta nel loro
            `title`. La data per esteso è già scritta qui accanto, e ripeterla
            due volte dentro i bottoni costava 180 px di barra — che a questa
            larghezza vuol dire una riga in più, cioè un'ora di calendario in
            meno. 👆 Restano larghi come un dito: li stringe il testo, non il
            bersaglio.
          */}
          <button
            type="button"
            className="piatto freccia"
            title={`Vai a ${giornoBreve(piuGiorni(data, -1))}`}
            aria-label={`Vai a ${giornoBreve(piuGiorni(data, -1))}`}
            onClick={() => vaiA(piuGiorni(data, -1))}
          >
            ←
          </button>
          <button type="button" className="piatto" onClick={() => vaiA(oggi())} disabled={data === oggi()}>
            Oggi
          </button>
          <button
            type="button"
            className="piatto freccia"
            title={`Vai a ${giornoBreve(piuGiorni(data, 1))}`}
            aria-label={`Vai a ${giornoBreve(piuGiorni(data, 1))}`}
            onClick={() => vaiA(piuGiorni(data, 1))}
          >
            →
          </button>
        </div>

        {/*
          ⚖️ I tre comandi del «come si guarda» stanno in un gruppo loro: quando
          la barra non ci sta su una riga sola deve andare a capo QUI, fra il
          giorno e il modo di guardarlo, non in mezzo ai selettori.
        */}
        <div className="comandi-vista">
          {/*
            📱 Sotto i 400 px la griglia a più campi non ci sta (§ 03.8): si passa
            a un campo per volta. Il selettore però resta visibile anche sul
            computer, perché «guarda solo il campo 4» è una cosa che si chiede
            anche da lì.
          */}
          <select
            aria-label="quali campi si vedono"
            value={campoScelto ?? ''}
            onChange={(e) => vaiA(data, e.target.value === '' ? null : e.target.value)}
          >
            <option value="">tutti i campi</option>
            {calendario?.courts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/*
            ⭐ QUANTO SI VEDE DELLA GIORNATA. «Tutta la giornata» è la vista normale
            e stringe le righe quanto serve perché dalle 08:00 alle 24:00 ci stia
            tutto senza scorrere; le altre due allargano, e allora scorre.
            ⚠️ Stringendo, sotto una certa altezza i nomi dei giocatori spariscono
            dai blocchi (non traboccano): è il prezzo, ed è il motivo per cui la
            via di ritorno c'è.
          */}
          <select
            aria-label="quanto si vede della giornata"
            value={vista}
            onChange={(e) => {
              const v = e.target.value as Vista;
              setVista(v);
              try {
                localStorage.setItem(CHIAVE_VISTA, v);
              } catch {
                /* senza memoria la scelta vale per questa visita: non è un guasto */
              }
            }}
          >
            {VISTE.map((v) => (
              <option key={v.valore} value={v.valore}>
                {v.testo}
              </option>
            ))}
          </select>

          {/*
            ⭐⭐ IL VERDE È UN PUNTO, IL ROSSO SONO PAROLE — e non è una simmetria
            mancata: è la regola.
            📏 «in tempo reale» costava 109 px per dire *va tutto bene*, cioè il
            caso in cui non c'è niente da dire. Quando invece il collegamento
            cade, le parole tornano: quello è lo stato che va LETTO, e un pallino
            grigio nessuno lo guarda.
            ⚖️ Le parole restano sempre nel documento: sparisce il modo di
            mostrarle, non l'informazione — chi legge a voce le sente comunque.
          */}
          {puoModificare && (
            <button
              type="button"
              className="piatto"
              onClick={() => {
                // Un pannello alla volta: la scheda aperta si chiude.
                setScheda(null);
                setNuova(null);
                setInagibile(true);
              }}
            >
              🌧️ Campo non utilizzabile
            </button>
          )}
          <span
            className={`spia ${attaccato ? 'accesa' : 'spenta'}`}
            role="status"
            title={
              attaccato
                ? 'Le modifiche degli altri arrivano subito.'
                : 'Il collegamento in tempo reale non c’è: la pagina si rilegge da sola ogni venti secondi.'
            }
          >
            <span className="punto" aria-hidden="true" />
            <span className={attaccato ? 'solo-a-voce' : undefined}>
              {attaccato ? 'in tempo reale' : 'rilettura periodica'}
            </span>
          </span>
        </div>
      </div>

      {isPending && <Attesa cosa="il calendario" />}
      <Guasto errore={error} />

      {/*
        ⚠️ Il rifiuto di uno spostamento si mostra QUI e non con un `alert`: il
        blocco è già tornato dov'era, e quello che manca è il motivo.
      */}
      {rifiuto && (
        <p className="guasto" role="alert">
          {rifiuto}{' '}
          <button type="button" className="piatto" onClick={() => setRifiuto(null)}>
            va bene
          </button>
        </p>
      )}

      {/* 🔔 Voce 49: le partite con l'acconto finite senza incasso aspettano qui la Segreteria. */}
      <DaSegnare
        apri={(p) => {
          vaiA(p.date);
          apriScheda(p.bookingId);
        }}
      />

      {calendario && (
        <Griglia
          calendario={calendario}
          data={data}
          campi={campi}
          /*
           * ⚠️ Il predefinito serve al primo disegno, quando la risposta non è
           *    ancora arrivata: senza, la griglia non saprebbe da che ora
           *    cominciare e lampeggerebbe a ogni cambio di giorno.
           */
          orario={calendario.hours ?? ORARIO_CIRCOLO_PREDEFINITO}
          vista={vista}
          puoModificare={puoModificare}
          apriScheda={apriScheda}
          /*
           * ⭐ La durata di partenza è la predefinita **se ci sta**: `spazio` è
           * quanto c'è da lì al primo ostacolo (⇒ `spazioLibero`). Senza questo
           * `min`, cliccando la mezz'ora libera prima di una partita la scheda
           * nasceva da 90′ e «Crea» la rifiutava a compilazione finita — cioè
           * l'altra metà del difetto che lui ha visto sulle celle verdi.
           */
          nuova={(courtId, time, fuoriFascia, spazio) =>
            apriNuova({
              courtId,
              time,
              durationMinutes: Math.min(DURATA_PREDEFINITA, spazio),
              fuoriFascia,
            })
          }
          sposta={(b: Prenotazione, courtId, time) => {
            setRifiuto(null);
            sposta.mutate(
              { id: b.id, daData: b.date, modifica: { courtId, time, date: data } },
              { onError: (e) => setRifiuto(e.message) },
            );
          }}
          cambiaDurata={(b, durationMinutes) => {
            setRifiuto(null);
            sposta.mutate(
              { id: b.id, daData: b.date, modifica: { durationMinutes } },
              { onError: (e) => setRifiuto(e.message) },
            );
          }}
        />
      )}

      {calendario && inagibile && (
        <CampoInagibile data={data} campi={calendario.courts} chiudi={() => setInagibile(false)} />
      )}

      {calendario && nuova && (
        <PannelloNuova
          nuova={nuova}
          data={data}
          campi={calendario.courts}
          maestri={calendario.instructors}
          prenotazioni={calendario.bookings}
          inCorso={crea.isPending || creaManutenzione.isPending || creaSerie.isPending}
          errore={crea.error ?? creaManutenzione.error ?? creaSerie.error}
          chiudi={() => {
            setNuova(null);
            crea.reset();
            creaManutenzione.reset();
          }}
          confermaManutenzione={(b) =>
            creaManutenzione.mutate(b, {
              onSuccess: (items) => {
                creaManutenzione.reset();
                /*
                 * ⚖️ La scheda si apre solo se il campo era UNO: con quattro
                 * non esiste «la» scheda da aprire, e aprirne una a caso
                 * nasconderebbe le altre tre invece di mostrare il lavoro.
                 * Con più campi si chiude, e le manutenzioni si vedono tutte
                 * sul calendario — che è dove sono.
                 */
                if (items.length === 1) apriScheda(items[0]!.id);
                else setNuova(null);
              },
            })
          }
          confermaSerie={(b) =>
            creaSerie.mutate(b, {
              onSuccess: (r) => {
                creaSerie.reset();
                if (b.caparra?.modo === 'lettore' && r.prima.caparraDettaglio?.status === 'pending') {
                  mandaAlLettore(r.prima.caparraDettaglio.id, r.prima.id);
                }
                /*
                 * 🔁 Si apre la PRIMA: è la prenotazione che si stava facendo,
                 * e le altre si vedono sul calendario dove sono. I giorni
                 * saltati restano scritti nel pannello che si apre (⇒ `Scheda`),
                 * perché sono l'unica cosa che nessun'altra schermata direbbe.
                 */
                /*
                 * 🏷️ Voce 10: il corso può partire da un altro giorno («Dal»).
                 * Allora si va a quel giorno, o la scheda si aprirebbe su un
                 * calendario che non la mostra.
                 */
                if (r.prima.date !== data) vaiA(r.prima.date);
                apriScheda(r.prima.id);
                // ⚠️ DOPO `apriScheda`, che azzera: l'ordine qui è la differenza
                //    fra vedere i giorni saltati e non vederli mai.
                setSaltate(r.saltate);
              },
            })
          }
          conferma={(b) =>
            crea.mutate(b, {
              onSuccess: (p) => {
                crea.reset();
                // 📟 🆕 Fase 3 (R9): «la paga qui, sul lettore» ⇒ l'importo parte subito verso il Solo.
                if (b.caparra?.modo === 'lettore' && p.caparraDettaglio?.status === 'pending') {
                  mandaAlLettore(p.caparraDettaglio.id, p.id);
                }
                /*
                 * ⭐ Lo stesso pannello passa alla scheda: appena creata, la cosa
                 * che serve è metterci dentro le persone. Prima erano DUE fogli
                 * in fila — uno si chiudeva e uno si apriva — ed era la terza
                 * delle «due schede» che lui ha visto.
                 */
                apriScheda(p.id);
              },
            })
          }
        />
      )}

      {calendario && scheda && (
        <Scheda
          id={scheda}
          data={data}
          saltate={saltate}
          campi={calendario.courts}
          maestri={calendario.instructors}
          prenotazioni={calendario.bookings}
          puoModificare={puoModificare}
          puoIncassare={puoIncassare}
          chiudi={() => {
            setScheda(null);
            setSaltate([]);
          }}
        />
      )}
    </section>
  );
}

/** `2026-10-07` → «ottobre»: il nome del mese dentro una frase. */
function meseDi(giorno: string): string {
  return mesePerEsteso(giorno.slice(0, 7)).split(' ')[0]!.toLowerCase();
}

/**
 * ⭐⭐ **LA PRENOTAZIONE NUOVA È LA STESSA SCHEDA, ANCORA SPENTA.**
 *
 * 🗣️ Richiesta sua del 17/09/2026: *«se all'inizio se ne aprono due per poter
 * poi cominciare a fare una prenotazione, si può massimizzare in un'apertura, e
 * dentro quella scheda si fa tutto… io vorrei fare tutto con un clic e riempire
 * la scheda»*.
 *
 * 🔄 **Cosa c'era prima, e perché non bastava.** Dal 16/09 il posto sullo
 * schermo era già uno solo — questo pannello — ma dentro ci passavano **due
 * moduli**: prima si sceglieva tipo, durata e titolo e si premeva «Crea», poi
 * compariva un'altra faccia, con altre sezioni, dove si mettevano le persone.
 * ⇒ Una prenotazione con quattro giocatori voleva **cinque scritture**: la
 *   creazione e poi un giocatore per volta.
 *
 * ✅ **Adesso**: le persone si scelgono **prima**, e partono **dentro** la
 * creazione (`players`, che la rotta accetta da sempre). Un clic sulla cella,
 * si riempie, si preme «Crea» — **una scrittura sola** — e la faccia non cambia:
 * le sezioni sono le stesse, nello stesso ordine, con lo stesso piede.
 *
 * ⚖️ Il pezzo che rende invisibile la giunta non è qui: è che «In campo» e
 *   «Dettagli» sono **gli stessi componenti** della scheda vera
 *   (⇒ `pezzi-scheda.tsx`). Due moduli che si somigliano si vede che sono due.
 */
function PannelloNuova({
  nuova,
  data,
  campi,
  maestri,
  prenotazioni,
  inCorso,
  errore,
  chiudi,
  conferma,
  confermaManutenzione,
  confermaSerie,
}: {
  nuova: Nuova;
  data: string;
  campi: Campo[];
  maestri: Maestro[];
  /** 🎾 Voce 9, Q3: le prenotazioni del giorno, per dire se il maestro è già altrove. */
  prenotazioni: readonly Prenotazione[];
  inCorso: boolean;
  errore: unknown;
  chiudi(): void;
  conferma(b: CreaPrenotazione): void;
  /**
   * ⚖️ Un secondo modo di confermare, e non un `conferma` che accetta tutt'e
   * due le forme: sono due gesti diversi (una prenotazione · N campi chiusi),
   * vanno su due rotte diverse e rendono due cose diverse. Un parametro che
   * cambia significato a seconda del tipo sarebbe la stessa ambiguità, spostata
   * dentro una funzione.
   */
  confermaManutenzione(b: CreaManutenzione): void;
  /** 🔁 E un terzo, per la stessa ragione: rende una cosa diversa dagli altri due. */
  confermaSerie(b: CreaSerieChiesta): void;
}): React.JSX.Element {
  const [v, setV] = useState<ValoriDettagli>({
    note: '',
    tipo: 'partita',
    maestro: '',
    durata: nuova.durationMinutes,
  });

  const [posti, setPosti] = useState<PostoScelto[]>([]);
  const contatore = useRef(0);
  /** 🌙 L'ultima richiesta partita: se il gestionale chiede la caparra, si rimanda con la scelta. */
  const [ultima, setUltima] = useState<CreaPrenotazione | null>(null);
  /** 🆕 Fase 3 (R7): lo stesso, per un campo fisso (una caparra sola per tutte le partite). */
  const [ultimaSerie, setUltimaSerie] = useState<CreaSerieChiesta | null>(null);

  /**
   * 🎓 19c — il CORSO nasce con un livello e con una fine: ogni settimana dal
   * giorno cliccato fino al giorno scelto, e ogni iscritto paga la quota del
   * mese del suo livello. La fine proposta è la dodicesima settimana, come per
   * ogni periodicità (⇒ `finePredefinita`).
   */
  const [livello, setLivello] = useState<LivelloCorso>('principiante');
  const [fineCorso, setFineCorso] = useState(() => finePredefinita(data, { cadenza: 'settimanale' }));
  /**
   * 🏷️ Voce 10 (27/09/2026, mockup approvato, scelta A). 🗣️ *«quando creo un
   * corso mi manca da mettere il nome del corso e … non mi fa deselezionare il
   * lunedì»*.
   *   · il **nome**: obbligatorio, come nell'area maestro;
   *   · il **«Dal»** si sceglie: con un martedì il corso va di martedì, alla
   *     stessa ora e sullo stesso campo. Prima bisognava chiudere e ricliccare
   *     su un martedì.
   */
  const [nomeCorso, setNomeCorso] = useState('');
  const [dal, setDal] = useState(data);
  const scegliDal = (g: string) => {
    if (!g) return;
    setDal(g);
    // Una fine prima dell'inizio farebbe un corso di una volta sola: si rifà.
    if (fineCorso < g) setFineCorso(finePredefinita(g, { cadenza: 'settimanale' }));
  };
  const tariffe = usaTariffeLezioni();

  /**
   * 🔁 **La periodicità parte SPENTA**, e non è timidezza: la stragrande
   * maggioranza delle prenotazioni è una sola volta, e una casella già spuntata
   * farebbe nascere dodici partite a chi non ha guardato.
   */
  const [ripete, setRipete] = useState<ValoriRipetizione>({
    attiva: false,
    cadenza: 'settimanale',
    giorni: [],
    fino: finePredefinita(data, { cadenza: 'settimanale' }),
  });

  /**
   * ⭐⭐ **SU QUALI CAMPI** — 17/09/2026, richiesta sua: *«quando si seleziona
   * manutenzione mi devi dare la possibilità di prenotare anche tutti e quattro
   * i campi, [...] o uno o due o tre»*.
   *
   * ⚖️ Parte da quello su cui ha cliccato, e si può togliere: cliccare la cella
   * sbagliata e accorgersene con la scheda aperta è normale, e obbligare a
   * chiudere e ricliccare sarebbe farglielo pagare.
   * ⛔ Ma almeno uno deve restare (⇒ `manca`), o «Crea» farebbe un gesto che non
   * fa niente e sembra riuscito.
   */
  const [campiScelti, setCampiScelti] = useState<string[]>([nuova.courtId]);
  const manutenzione = v.tipo === 'manutenzione';
  const nomeCampo = (id: string) => campi.find((c) => c.id === id)?.name ?? '';

  const capienza = CAPIENZA[v.tipo];
  const pieno = posti.length >= capienza;
  /**
   * ⭐ 17/09/2026 — **«In campo» sparisce quando non c'è nessun campo da
   * riempire**, cioè sulla manutenzione (`CAPIENZA` 0).
   *
   * ⚖️ Non è pulizia: quella sezione diceva *«Non c'è ancora nessuno. Si può
   * creare anche così e metterli dopo»*, che su una manutenzione è **falso** —
   * dopo non si mette nessuno, mai. Una riga che promette una cosa impossibile
   * è peggio di una riga assente, e con la scelta dei campi appena sopra si
   * legge come se le due cose fossero alternative.
   * ⛔ Ma se qualcuno c'è già — si sceglievano le persone e poi si è cambiato
   *   tipo in manutenzione — la sezione RESTA: è l'unico posto da cui toglierle,
   *   e nasconderla lascerebbe «togline 4» senza il modo di toglierne nemmeno
   *   uno.
   */
  const inCampoHaSenso = capienza > 0 || posti.length > 0;
  // 🔒 Cosa non può mancare, per questo tipo (⇒ `pezzi-scheda.tsx`).
  //    Più una cosa che solo QUESTA scheda può chiedere: su quali campi.
  const corso = v.tipo === 'corso';
  const conOspiti = posti.some((g) => g.memberId === null);
  const manca =
    manutenzione && campiScelti.length === 0
      ? 'Scegli almeno un campo: senza, non c’è niente da chiudere.'
      : corso && conOspiti
        ? 'Al corso si iscrivono i soci: togli l’ospite, e mettilo poi su una volta sola.'
        : corso && nomeCorso.trim().length < 2
          ? 'Dai un nome al corso: si vede nella griglia, nella scheda e nei soldi del maestro.'
          : cosaManca(v.tipo, posti.length, v.maestro);
  /** Il giorno da cui parte quello che si crea: per il corso, il «Dal». */
  const inizio = corso ? dal : data;

  /**
   * 💶 19c — **L'IMPORTO A TESTA SI VEDE PRIMA DI «CREA»** per lezione,
   * allenamento e corso: dipende solo da quanti sono (o dal livello), non dal
   * campo né dall'ora, quindi si sa già adesso. Lo calcola la stessa funzione
   * che usa il server (⇒ `prezzoDaListinoLezioni`), così la cifra che si legge
   * qui è quella che finirà in colonna.
   */
  const aListino = siPrezzaColListinoLezioni(v.tipo);
  const prezzo =
    siPrezzaColListinoLezioni(v.tipo) && tariffe.data
      ? prezzoDaListinoLezioni(tariffe.data.items, v.tipo, posti.length, corso ? livello : null)
      : null;
  const volteCorso = corso ? giorniDellaSerie(dal, { cadenza: 'settimanale', giorni: [] }, fineCorso) : [];
  const mesi = mesiDelCorso(volteCorso);
  const nomiMesi = [...new Set(volteCorso.map((g) => meseDi(g)))];
  // Cambiando tipo la capienza può ABBASSARSI (la manutenzione non ha gente in
  // campo): allora il server rifiuterebbe, e conviene dirlo prima di premere.
  const troppi = posti.length > capienza;

  return (
    <Pannello etichetta="La prenotazione nuova" chiudi={chiudi}>
      {/*
        ⭐ LA TESTATA SEGUE LE STESSE REGOLE DELLA SCHEDA VERA — il nome di una
        prenotazione è il suo TIPO — così quando «Crea» riesce non si muove
        niente: è la stessa riga, con dentro la stessa cosa. E cambiando il tipo
        nel modulo qui sotto, cambia mentre si guarda.
      */}
      <TestaPannello
        titolo={etichettaTipo(v.tipo)}
        /* ⚖️ Con più campi scelti il nome singolo direbbe il falso: la testata
             dice quello che si sta per creare, non la cella da cui si è
             partiti. */
        sotto={`${manutenzione ? descriviCampi(campiScelti.map(nomeCampo)) : nomeCampo(nuova.courtId)} · ${giornoPerEsteso(inizio)} · ${nuova.time}–${oraPiuMinuti(nuova.time, v.durata)} · ${v.durata} minuti`}
        chiudi={chiudi}
      />

      <div className="corpo-pannello">
        {/*
          ⭐⭐ SI DICE PRIMA DI SALVARE, non dopo.
          ⚖️ Quest'ora è dentro l'orario del circolo ma fuori da ogni fascia: il
          server la accetta dalla segreteria e la rifiuterebbe a un socio. Chi
          la sta prenotando deve sapere **quale delle due cose sta facendo** —
          altrimenti la sola differenza fra un gesto normale e un'apertura
          straordinaria è il colore della cella su cui ha cliccato.
          📌 *Un permesso in più che non si vede non è un permesso: è una
             sorpresa che aspetta.*
        */}
        {/* ⚠️ Vale per il giorno cliccato: se il corso parte da un altro giorno, decide il server. */}
        {nuova.fuoriFascia && inizio === data && (
          <p className="avvertenza">
            <strong>Quest’ora non è in nessuna fascia prenotabile.</strong> I soci qui non prenotano da soli:
            la stai aprendo tu. Il campo è libero e il circolo è aperto.
          </p>
        )}

        {errore instanceof ErroreApi &&
        (errore.codice === 'CAPARRA_DA_SCEGLIERE' || errore.codice === 'NON_NEL_BOT') &&
        (ultima || ultimaSerie) ? (
          <SceltaCaparra
            messaggio={errore.message}
            dettagli={dettagliCaparraDaScegliereSchema.safeParse(errore.dettagli).data ?? null}
            inCorso={inCorso}
            prenota={(caparra) =>
              ultimaSerie ? confermaSerie({ ...ultimaSerie, caparra }) : conferma({ ...ultima!, caparra })
            }
          />
        ) : (
          <Guasto errore={errore} />
        )}

        {/* ── che cos'è: lo stesso modulo della scheda vera, e si sceglie PRIMA
             perché è il tipo a dire chi ci vuole e quanti ci stanno ────────── */}
        <h3 className="titolo-sezione">Che cos’è</h3>
        <ModuloDettagli
          idModulo={ID_NUOVA}
          v={v}
          cambia={(c) => setV((x) => ({ ...x, ...c }))}
          maestri={maestri}
          livello={{ valore: livello, cambia: setLivello }}
          maestroDaTenere={null}
          invia={() => {
            if (troppi || manca !== null) return;
            if (manutenzione) {
              confermaManutenzione({
                courtIds: campiScelti,
                date: data,
                time: nuova.time,
                durationMinutes: v.durata,
                notes: v.note.trim() === '' ? null : v.note.trim(),
              });
              return;
            }
            /*
             * 🔁 Due rotte, e la scelta si fa QUI: una prenotazione sola rende
             * una scheda, una serie rende quante ne sono nate e i giorni
             * saltati. Una rotta che rendesse l'una o l'altra a seconda di una
             * casella sarebbe una rotta di cui chi la chiama non sa cosa gli
             * tornerà.
             */
            const comune = {
              courtId: nuova.courtId,
              date: inizio,
              time: nuova.time,
              durationMinutes: v.durata,
              type: v.tipo,
              // 🔄 Una prenotazione nasce senza titolo: si chiama col suo tipo
              //    (⇒ `pezzi-scheda.tsx`, 17/09/2026). 🏷️ Il corso no (voce 10):
              //    vive mesi e si sceglie da un elenco, e ha il suo nome.
              title: corso ? nomeCorso.trim() : null,
              instructorId: v.maestro === '' ? null : v.maestro,
              notes: v.note.trim() === '' ? null : v.note.trim(),
              /*
               * ⭐ LE PERSONE PARTONO CON LA PRENOTAZIONE, in una scrittura
               * sola. Prima erano una chiamata a testa DOPO la creazione: se
               * una falliva restava una partita mezza riempita, e nessuno
               * sapeva quale metà.
               */
              players: posti.map((g) => ({
                memberId: g.memberId,
                guestName: g.memberId === null ? g.nome : null,
                amountDueCents: null,
              })),
            };
            // 🎓 19c: il corso è sempre una serie — ogni settimana, fino alla sua fine.
            if (corso) {
              const serie: CreaSerieChiesta = {
                ...comune,
                ripeti: { cadenza: 'settimanale', giorni: [], fino: fineCorso },
                livello,
              };
              // Come per ogni serie: se il gestionale chiede la caparra, si rimanda questa.
              setUltima(null);
              setUltimaSerie(serie);
              confermaSerie(serie);
            } else if (ripete.attiva) {
              const serie: CreaSerieChiesta = {
                ...comune,
                ripeti: { cadenza: ripete.cadenza, giorni: ripete.giorni, fino: ripete.fino },
              };
              setUltima(null);
              setUltimaSerie(serie);
              confermaSerie(serie);
            } else {
              setUltimaSerie(null);
              setUltima(comune);
              conferma(comune);
            }
          }}
        />
        {/* 🎾 Voce 9, Q3 (scelta A): per la segreteria è un avviso, non un blocco. */}
        {vuoleMaestro(v.tipo) && (
          <AvvisoMaestroAltrove
            prenotazioni={prenotazioni}
            maestroId={v.maestro}
            maestri={maestri}
            campi={campi}
            courtId={nuova.courtId}
            date={inizio}
            time={nuova.time}
            durata={v.durata}
          />
        )}

        {/*
          🔁⭐⭐ **LA PERIODICITÀ STA QUI, ATTACCATA A «CHE COS'È»** — 17/09/2026,
            richiesta sua: *«la possibilità di rendere una prenotazione
            periodica»*.
          ⚖️ Perché qui e non in fondo: *quante volte* è una proprietà della
            COSA, come il tipo e la durata — non un dettaglio da mettere dopo
            aver scelto la gente. E perché è qui che si decide **quante
            scritture** farà il bottone «Crea»: una decisione di quella portata
            non può stare sotto a tutto il resto.
          ⛔ **Non sulla manutenzione**, ed è dichiarato: lì un gesto solo chiude
            già N campi, e incrociare le due cose farebbe nascere N×M righe da un
            clic. Chi deve lavare i campi ogni lunedì lo fa lunedì per lunedì,
            finché non lo chiede.
        */}
        {/* 🎓 19c: il corso non «si ripete a scelta» — HA delle date, dal giorno
            cliccato a una fine, ogni settimana. Il resto della periodicità
            resta per tutti gli altri tipi. */}
        {corso ? (
          <>
            <label className="campo">
              <span>Nome del corso</span>
              <input
                value={nomeCorso}
                onChange={(e) => setNomeCorso(e.target.value)}
                placeholder="per esempio: Principianti del lunedì sera"
                maxLength={120}
                autoComplete="off"
              />
            </label>
            <h3 className="titolo-sezione">Le date del corso</h3>
            <p className="tenue piccolo">
              Ogni <strong>{giornoDellaSettimana(dal)}</strong> alle {nuova.time}, sul{' '}
              {nomeCampo(nuova.courtId)}.
            </p>
            <div className="date-corso">
              <CampoData etichetta="Dal" value={dal} min={oggi()} onChange={scegliDal} />
              <CampoData etichetta="Al" value={fineCorso} min={dal} onChange={(g) => g && setFineCorso(g)} />
            </div>
            {dal !== data && (
              <p className="tenue piccolo">
                🔄 Hai cliccato su {giornoPerEsteso(data)}: la prima volta diventa{' '}
                <strong>{giornoPerEsteso(dal)}</strong>, alla stessa ora e sullo stesso campo. Se quel giorno
                il campo è occupato, il gestionale lo dice.
              </p>
            )}
            <p className="tenue piccolo">
              {volteCorso.length} {volteCorso.length === 1 ? 'volta' : 'volte'},{' '}
              {nomiMesi.length === 1 ? `in ${nomiMesi[0]}` : `da ${nomiMesi[0]} a ${nomiMesi.at(-1)}`}:{' '}
              <strong>
                {mesi} {mesi === 1 ? 'mese' : 'mesi'}
              </strong>
              .
            </p>
          </>
        ) : (
          !manutenzione && (
            <>
              <h3 className="titolo-sezione">Quante volte</h3>
              <ModuloRipetizione dal={data} v={ripete} cambia={(c) => setRipete((x) => ({ ...x, ...c }))} />
            </>
          )
        )}

        {/*
          ⭐⭐ SU QUALI CAMPI — c'è SOLO per la manutenzione, ed è voluto.
          ⚖️ Una partita, una lezione, un corso e un torneo stanno su UN campo:
            sono la gente che ci gioca. Una manutenzione è un campo CHIUSO, e
            chiuderne quattro è un gesto solo — il circolo lava i campi, non
            «prenota quattro volte».
          ⛔ Mostrarlo per tutti i tipi vorrebbe dire offrire di creare quattro
            partite uguali su quattro campi, che non è una cosa che esiste.
        */}
        {manutenzione && (
          <>
            <h3 className="titolo-sezione">
              Su quali campi
              <span className="tenue piccolo">
                {campiScelti.length} di {campi.length}
              </span>
            </h3>
            <div className="scelta-campi">
              {campi.map((c) => (
                <label key={c.id}>
                  <input
                    type="checkbox"
                    checked={campiScelti.includes(c.id)}
                    onChange={(e) =>
                      setCampiScelti((x) => (e.target.checked ? [...x, c.id] : x.filter((y) => y !== c.id)))
                    }
                  />
                  {c.name}
                </label>
              ))}
            </div>
            <p className="tenue piccolo">
              {campiScelti.length > 1
                ? `Nascono ${campiScelti.length} manutenzioni, una per campo, tutte alle ${nuova.time}. Se anche uno solo di questi campi non è libero non ne nasce nessuna, e ti si dice quali.`
                : 'Se ne possono chiudere più di uno nello stesso orario: spunta gli altri.'}
            </p>
          </>
        )}

        {/* ── chi entra in campo, PRIMA che la prenotazione esista ─────────── */}
        {inCampoHaSenso && (
          <>
            <h3 className="titolo-sezione">
              {corso ? 'Iscritti' : 'In campo'}
              <span className="tenue piccolo">
                {posti.length} di {capienza}
              </span>
            </h3>
            {/* 🎓 19c: da quale casella del listino viene l'importo a testa — la
                riga del mockup approvato. Verde se c'è, ambra se non è deciso. */}
            {prezzo !== null && (posti.length > 0 || corso) && (
              <p className={prezzo.priceCents === null ? 'da-dove non-deciso' : 'da-dove'}>
                {prezzo.perche}
                {prezzo.priceCents === null && ' L’importo si scrive a mano, dalla scheda.'}
              </p>
            )}
            {posti.length === 0 ? (
              /**
               * 🚨⭐⭐ **LA RASSICURAZIONE SI DICE SOLO QUANDO È VERA — misurato
               * sull'app viva il 18/09/2026.**
               *
               * 📏 Qui c'era scritta sempre, e su una **partita** vuota diceva
               *   il falso mentre «Crea» era **spento**: nello stesso pannello
               *   si leggeva *«si può creare anche così: il campo resta
               *   tenuto»* e, tre righe sotto, *«una partita vuole almeno una
               *   persona in campo»*. Due frasi opposte nello stesso istante, e
               *   quella che tranquillizza era quella sbagliata.
               * ⛔ Il costo non è la confusione: è che chi legge **se ne va**
               *   convinto di aver tenuto il campo, e il campo non è tenuto da
               *   nessuno. Un torneo o una manutenzione invece si creano vuoti
               *   davvero, ed è per quelli che la frase era stata scritta.
               * ⇒ Chi decide è `manca`, la stessa riga che spegne il bottone:
               *   così le due non possono più dire cose diverse.
               * 📌 *Una frase che rassicura va legata alla stessa condizione che
               *    permette il gesto, o rassicura proprio quando non dovrebbe.*
               */
              <p className="tenue">
                Non c’è ancora nessuno.
                {manca === null && ' Si può creare anche così e metterli dopo: il campo resta tenuto.'}
              </p>
            ) : (
              <table className="tabella in-campo">
                <tbody>
                  {posti.map((g) => (
                    <tr key={g.chiave}>
                      {/* ⚠️ Le stesse classi della scheda vera (`chi`, `carico`, `via`):
                          sono quelle che sul telefono ripiegano la riga su due
                          piani invece di accavallare nome e cifra. */}
                      <td className="chi">
                        {g.nome}
                        {g.memberId === null && <span className="tenue piccolo"> · ospite</span>}
                      </td>
                      {prezzo !== null && (
                        <td className="carico tenue piccolo">
                          {prezzo.priceCents === null
                            ? '–'
                            : `${euro(prezzo.priceCents)}${corso ? ' al mese' : ''}`}
                        </td>
                      )}
                      <td className="via azioni-riga">
                        <button
                          type="button"
                          className="piatto pericolo"
                          onClick={() => setPosti((x) => x.filter((y) => y.chiave !== g.chiave))}
                        >
                          Togli
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* 💶 Su partita e torneo gli importi non si toccano qui, ed è una
            scelta: il loro listino li sa solo quando la prenotazione esiste
            (dipendono da campo, ora e durata definitivi). Lezione, allenamento e
            corso invece li dicono già sopra (19c), e si cambiano dalla scheda. */}
            {posti.length > 0 && !aListino && (
              <p className="tenue piccolo">
                Gli importi a carico li propone il listino appena la prenotazione c’è.
              </p>
            )}
            {posti.length > 0 && aListino && !corso && prezzo?.priceCents != null && (
              <p className="tenue piccolo">
                {v.tipo === 'lezione' ? 'La lezione' : 'L’allenamento'} incassa{' '}
                {euro(prezzo.priceCents * posti.length)}. Se entra o esce qualcuno, gli importi si
                ricalcolano.
              </p>
            )}
            {posti.length > 0 && corso && (
              <div className="riepilogo-corso">
                {prezzo?.priceCents != null ? (
                  <>
                    Ogni iscritto:{' '}
                    <strong>
                      {mesi} {mesi === 1 ? 'mese' : 'mesi'} × {euro(prezzo.priceCents)} ={' '}
                      {euro(mesi * prezzo.priceCents)}
                    </strong>{' '}
                    a carico, una quota per mese ({nomiMesi.join(', ')}).
                  </>
                ) : (
                  <>
                    Ogni iscritto:{' '}
                    <strong>
                      {mesi} {mesi === 1 ? 'mese' : 'mesi'} × la quota scritta a mano
                    </strong>
                    .
                  </>
                )}
                <br />
                <span className="tenue piccolo">
                  La quota va sulla prima volta di ogni mese. Un mese con cinque {giornoDellaSettimana(dal)}{' '}
                  paga la stessa quota di uno con quattro.
                </span>
              </div>
            )}

            {troppi && (
              <p className="guasto">
                In campo ci stanno {capienza} persone e qui ce ne sono {posti.length}: togline{' '}
                {posti.length - capienza}, o rimetti il tipo di prima.
              </p>
            )}

            {!pieno && (
              <AggiungiGiocatore
                giaInCampo={sociGiaInCampo(posti)}
                aggiungi={(g, nome) =>
                  setPosti((x) =>
                    // ⛔ Due volte la stessa persona in campo sarebbe una persona con
                    //    due posti, e il giorno dell'incasso due addebiti.
                    g.memberId !== null && x.some((y) => y.memberId === g.memberId)
                      ? x
                      : [...x, { chiave: `p${contatore.current++}`, memberId: g.memberId, nome }],
                  )
                }
              />
            )}
            {pieno && !troppi && <p className="tenue">In campo non ci sta nessun altro.</p>}
          </>
        )}

        {/* ── le note, in coda: sono la postilla, non una domanda ─────────── */}
        <h3 className="titolo-sezione">Note</h3>
        <CampoNote idModulo={ID_NUOVA} v={v} cambia={(c) => setV((x) => ({ ...x, ...c }))} />
      </div>

      {/* Le azioni stanno nel piede fermo, come nella scheda: è lo stesso posto
          per lo stesso genere di gesto, e non si va a cercarlo scorrendo.
          ⭐ E il motivo per cui «Crea» è spento sta ACCANTO a «Crea», non in
             cima al pannello: un bottone spento senza il perché a fianco è il
             modo più diretto per far credere che la schermata sia rotta. */}
      <footer className={manca === null ? 'piede-pannello' : 'piede-pannello con-motivo'}>
        {manca !== null && <p className="motivo">{manca}</p>}
        <button type="button" className="piatto" onClick={chiudi}>
          Lascia stare
        </button>
        <span className="riempi" />
        <button type="submit" form={ID_NUOVA} disabled={inCorso || troppi || manca !== null}>
          {inCorso ? 'Sto salvando…' : 'Crea'}
        </button>
      </footer>
    </Pannello>
  );
}

/** Una persona scelta prima che la prenotazione esista: qui non ha un `id`. */
interface PostoScelto {
  /** Serve solo a React per non confondere due ospiti omonimi. */
  chiave: string;
  memberId: string | null;
  nome: string;
}

/**
 * 🌙 **Una partita di sera prenotata dalla Segreteria** (sezione 9 del mockup
 * approvato, 25/09/2026): la caparra si risolve adesso. Il gestionale la chiede
 * solo quando serve — di sera, a caparra accesa, con un socio in campo.
 */
function SceltaCaparra({
  messaggio,
  dettagli,
  inCorso,
  prenota,
}: {
  messaggio: string;
  dettagli: DettagliCaparraDaScegliere | null;
  inCorso: boolean;
  prenota(c: CaparraDallaSegreteria): void;
}): React.JSX.Element {
  /**
   * 🆕 Fase 2 (26/09/2026) — **chi non è nel bot riceve l'invito, non il link.**
   * 🗣️ La regola del committente per la Segreteria: *«ti mando il link
   * affinché tu puoi farti da solo la prenotazione»*. Il link della caparra
   * arriva solo nel bot: per chi non c'è, la scelta non si mostra viva.
   */
  const nelBot = dettagli?.inBot ?? true;
  const fisso = dettagli?.series === true;
  const [modo, setModo] = useState<'link' | 'cash' | 'lettore' | 'card' | 'esente'>(nelBot ? 'link' : 'cash');
  const [motivo, setMotivo] = useState('');
  // 🆕 Fase 3 (R9): il lettore Solo, se si usa da qui.
  const lettore = useQuery({
    queryKey: ['stato-lettore'],
    queryFn: () => chiama<StatoDelLettore>('GET', '/deposits/reader-status'),
    staleTime: 60_000,
  });
  const conLettore = lettore.data?.ready === true;
  return (
    <div className="finestra-caparra">
      <p>
        <span className="pill-caparra pill-attesa">
          {dettagli?.reason === 'no_show'
            ? '🔒 No-show ripetuti'
            : fisso
              ? 'Campo fisso di sera'
              : 'Fascia serale'}
        </span>{' '}
        {messaggio}
      </p>
      {dettagli && !nelBot && (
        <div className="invito-caparra">
          <p>
            <strong>{dettagli.memberName} non è nel bot</strong>, quindi il link dell’acconto non gli può
            arrivare. Mandagli l’invito: si registra e il campo se lo prenota da solo, acconto compreso.
            Finché non prenota, il campo resta libero per tutti.
          </p>
          <InvitoBot
            id={dettagli.memberId}
            nome={dettagli.firstName ?? dettagli.memberName}
            nomeIntero={dettagli.memberName}
          />
        </div>
      )}
      <div className="scelte-caparra">
        <label className={nelBot ? undefined : 'spenta'}>
          <input type="radio" checked={modo === 'link'} disabled={!nelBot} onChange={() => setModo('link')} />
          <span>
            <strong>Mando il link al socio nel bot</strong>
            <br />
            <span className="tenue piccolo">
              {!nelBot
                ? 'Non è nel bot: il link non gli può arrivare.'
                : dettagli
                  ? `Quando tocca «Paga l’acconto» ha ${dettagli.payMinutes} minuti. Se non lo tocca entro ${dettagli.tapMinutes} minuti dall’arrivo del messaggio, ${fisso ? 'le partite tornano libere' : 'il campo torna libero'}.`
                  : 'Il campo resta tenuto finché il socio paga, come quando prenota lui.'}
            </span>
          </span>
        </label>
        <label>
          <input type="radio" checked={modo === 'cash'} onChange={() => setModo('cash')} />
          <span>
            <strong>L’ho incassato qui, in contanti</strong>
          </span>
        </label>
        {conLettore && (
          <label>
            <input type="radio" checked={modo === 'lettore'} onChange={() => setModo('lettore')} />
            <span>
              <strong>📟 La paga qui, con la carta sul lettore</strong>
              <br />
              <span className="tenue piccolo">
                Il gestionale manda l’importo al lettore: il socio avvicina la carta, e si segna da solo.
              </span>
            </span>
          </label>
        )}
        <label>
          <input type="radio" checked={modo === 'card'} onChange={() => setModo('card')} />
          <span>
            <strong>L’ho incassato qui, con la carta</strong>
            {conLettore && (
              <>
                <br />
                <span className="tenue piccolo">Se l’hai già battuta sul lettore a mano.</span>
              </>
            )}
          </span>
        </label>
        <label>
          <input type="radio" checked={modo === 'esente'} onChange={() => setModo('esente')} />
          <span>
            <strong>Non serve</strong> · un caso particolare
          </span>
        </label>
      </div>
      {modo === 'esente' && (
        <label className="campo">
          <span>Perché non serve (resta scritto nei resoconti)</span>
          <input value={motivo} onChange={(ev) => setMotivo(ev.target.value)} />
        </label>
      )}
      <button
        type="button"
        disabled={inCorso || (modo === 'esente' && motivo.trim().length < 3)}
        onClick={() =>
          prenota(
            modo === 'link'
              ? { modo: 'link' }
              : modo === 'lettore'
                ? { modo: 'lettore' }
                : modo === 'esente'
                  ? { modo: 'esente', motivo: motivo.trim() }
                  : { modo: 'banco', metodo: modo },
          )
        }
      >
        {inCorso ? 'Un attimo…' : 'Prenota'}
      </button>
    </div>
  );
}
